import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Form, Progress, Row, Space, Switch, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { CopyOutlined, FileExcelOutlined, PlusOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { Users, Home, GraduationCap, PhoneCall } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useAuthStore } from '@stores/auth.store';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useStudents } from '@hooks/tenant/useStudents';
import { useModuleLimit } from '@hooks/tenant/useModuleLimit';
import { useCreateStudent } from '@hooks/tenant/useCreateStudent';
import { useUpdateStudent } from '@hooks/tenant/useUpdateStudent';
import { useDirectoryStatusChangeFlow } from '@hooks/tenant/useDirectoryStatusChangeFlow';
import { useTenantClasses } from '@hooks/tenant/useTenantClasses';
import { useCampusBranches } from '@hooks/tenant/useCampusBranches';
import { useStudentBadges } from '@hooks/tenant/useStudentBadges';
import { STUDENT_BADGES_DEFAULT_BRANCH_ID } from '@constants/firestorePaths';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { fetchStudentsOnce, checkStudentIdExists, getSchoolInitials, fetchStudentsPage } from '@services/registry';
import { exportToExcel, exportToPdf } from '@/utils/reportExporter';
import { DashboardContainer, KPICardsGrid, StudentsHeaderActions, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from './style';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppPasswordInput, AppTextArea } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { Button } from '@components/ui/Button';
import AppAvatar from '@/components/ui/AppAvatar';
import styled, { createGlobalStyle } from 'styled-components';

const { Text, Link } = Typography;

const StudentRegisterModalStyles = createGlobalStyle`
  .student-register-modal .ant-modal-content {
    max-height: 96vh;
    display: flex;
    flex-direction: column;
  }

  .student-register-modal .ant-modal-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: inherit;
  }

  .student-register-modal .ant-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: inherit;
    margin-top: auto;
    padding-top: 14px;
  }

  .student-register-modal .ant-modal-body {
    flex: 1;
    overflow: auto;
    max-height: calc(96vh - 160px);
    padding: 16px;
    padding-right: 10px;
    background: inherit;

    scrollbar-width: thin;
    scrollbar-color: rgba(138, 152, 167, 0.55) transparent;
  }

  .student-register-modal .ant-modal-body::-webkit-scrollbar {
    width: 8px;
  }

  .student-register-modal .ant-modal-body::-webkit-scrollbar-track {
    background: transparent;
  }

  .student-register-modal .ant-modal-body::-webkit-scrollbar-thumb {
    background: rgba(138, 152, 167, 0.45);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .student-register-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 152, 167, 0.65);
    border: 2px solid transparent;
    background-clip: content-box;
  }
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;

    &.ant-picker {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#ffffff')};
        border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
        border-radius: 12px;
        height: 44px;
        padding: 0 12px;
        font-family: var(--font-family-lufga);
        display: flex;
        align-items: center;
        box-shadow: none;
        transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    &.ant-picker:hover {
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#8A98A7' : '#8A98A7')};
    }

    &.ant-picker-focused {
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 2px rgba(240, 74, 74, 0.15);
    }

    .ant-picker-input > input {
        font-family: var(--font-family-lufga);
        height: 42px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : 'rgba(0, 0, 0, 0.88)')};
    }

    .ant-picker-input > input::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.35)')};
    }

    .ant-picker-suffix,
    .ant-picker-clear {
        display: flex;
        align-items: center;
    }
`;

const FormSectionCard = styled.div`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 16px;
    padding: 18px 18px 8px 18px;
    margin-bottom: 14px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-family-lufga);
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 14px;
`;

const CardHeaderIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(240, 74, 74, 0.18)' : '#FFF0F1')};
    color: ${({ theme }) => theme.colors.primary};
`;

const ProfileImageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const ProfileImagePreview = styled.div`
    width: 84px;
    height: 84px;
    border-radius: 14px;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#fafafa')};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const UploadHelp = styled.div`
    margin-top: 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

const SectionTitle = styled.div`
    margin-top: 2px;
    margin-bottom: 10px;
`;

const SectionDivider = styled.div`
    margin-top: 8px;
    height: 1px;
    background: ${({ theme }) => theme.colors.border.light};
`;

function toDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

function toDayjs(value) {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (typeof value?.toDate === 'function') {
        const out = dayjs(value.toDate());
        return out.isValid() ? out : null;
    }
    const out = dayjs(value instanceof Date ? value : value);
    return out.isValid() ? out : null;
}

function parseImportDate(value, acceptedFormats) {
    if (value === null || value === undefined || value === '') return '';
    if (value instanceof Date && !Number.isNaN(value.getTime())) return dayjs(value).format('YYYY-MM-DD');
    if (typeof value === 'number') {
        const dateObj = XLSX.SSF.parse_date_code(value);
        if (!dateObj) return String(value);
        return dayjs(new Date(dateObj.y, dateObj.m - 1, dateObj.d)).format('YYYY-MM-DD');
    }
    const raw = String(value).trim();
    const parsedWithTenantFormat = dayjs(raw, acceptedFormats, true);
    if (parsedWithTenantFormat.isValid()) return parsedWithTenantFormat.format('YYYY-MM-DD');
    const fallbackParsed = dayjs(raw);
    return fallbackParsed.isValid() ? fallbackParsed.format('YYYY-MM-DD') : raw;
}

function normalizeHeaderKey(raw) {
    return String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Import row: Badge # + Badge Year → Firestore badge id (year defaults to current calendar year if # only). */
function resolveBadgeIdFromImportRow(row, badgeRows, { defaultBadgeId, defaultCalendarYear, classBranchId }) {
    const numRaw = row['badge number'] ?? row['badge #'];
    const yearRaw = row['badge year'];
    const numStr = numRaw !== undefined && numRaw !== null && String(numRaw).trim() !== '' ? String(numRaw).trim() : '';
    const yearStr = yearRaw !== undefined && yearRaw !== null && String(yearRaw).trim() !== '' ? String(yearRaw).trim() : '';

    if (!numStr && !yearStr) {
        return defaultBadgeId || null;
    }
    if (!numStr) {
        throw new Error('Badge # is required when Badge Year is filled');
    }
    const num = Number(numStr);
    if (!Number.isFinite(num)) {
        throw new Error(`Invalid Badge # "${numStr}"`);
    }
    const year = yearStr ? Number(yearStr) : Number(defaultCalendarYear);
    if (!Number.isFinite(year)) {
        throw new Error(`Invalid Badge Year "${yearStr}"`);
    }
    const match = (badgeRows || []).find(
        (b) =>
            Number(b.badgeNumber) === num &&
            Number(b.year) === year &&
            (!classBranchId || !b.branchId || b.branchId === classBranchId)
    );
    if (!match?.id) {
        throw new Error(`No badge for #${num} and year ${year}. Create it under Classes & Badge.`);
    }
    return match.id;
}

const generateNextStudentId = async (tId, schoolName) => {
    const initials = getSchoolInitials(schoolName);
    const year = dayjs().year();
    const prefix = `${initials}-${year}-`;

    const students = await fetchStudentsOnce({ tenantId: tId });

    let maxSerial = 1000;
    const regex = new RegExp(`^${initials}-${year}-(\\d+)$`);
    students.forEach((s) => {
        const sid = s.studentId;
        if (sid) {
            const match = sid.match(regex);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxSerial) {
                    maxSerial = num;
                }
            }
        }
    });

    return `${prefix}${maxSerial + 1}`;
};

const Students = () => {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId;
    const phoneLinkMode = useAuthStore((s) => s.user?.preferences?.phoneLinkMode || 'call');
    const phoneLinkTemplate = useAuthStore((s) => s.user?.preferences?.phoneLinkTemplate || 'https://wa.me/{phone}');
    const phoneCountryCode = useAuthStore((s) => s.user?.preferences?.phoneCountryCode || '92');
    const { dateFormat, dateInputFormats } = useTenantDateFormat();
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const { rows, total, loading, kpis } = useStudents({ tenantId, page: currentPage, pageSize: currentPageSize });
    const { exceeded: limitExceeded } = useModuleLimit({ moduleKey: 'students', currentCount: total });
    const { rows: tenantClasses } = useTenantClasses({ tenantId });
    const { branches } = useCampusBranches({ tenantId });
    const {
        badgeRows,
        loading: badgesLoading,
        defaultBadgeId,
        badgeFormOptions,
        badgeRowById,
        badgeYearDefault,
    } = useStudentBadges({ tenantId });

    const [searchText, setSearchText] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState('create'); // create | edit
    const [editingStudent, setEditingStudent] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [form] = Form.useForm();

    const [studentIdUnique, setStudentIdUnique] = useState(true);
    const [checkingStudentId, setCheckingStudentId] = useState(false);
    const watchedStudentId = Form.useWatch('studentId', form);

    const showDownload = useMemo(() => {
        if (!user) return false;
        if (
            user.role === 'tenant' ||
            user.role === 'owner' ||
            user.role === 'admin' ||
            user.staffRole === 'admin' ||
            user.permissions?.includes('*')
        ) return true;
        return user.permissions?.includes('studentReport.view') || user.permissions?.includes('studentReport.full');
    }, [user]);

    const handleFetchAllStudents = async (onProgress) => {
        let allRows = [];
        let cursor = null;
        let hasMore = true;
        const pageSize = 50;
        let currentLoaded = 0;

        while (hasMore) {
            const res = await fetchStudentsPage({
                tenantId,
                pageSize,
                cursor,
            });
            allRows = [...allRows, ...res.rows];
            currentLoaded += res.rows.length;

            const totalEst = searchText ? filteredRows.length : (res.total || kpis.totalStudents || 100);
            onProgress(Math.min(currentLoaded, totalEst), totalEst);

            if (res.rows.length < pageSize || !res.nextCursor) {
                hasMore = false;
            } else {
                cursor = res.nextCursor;
            }
        }

        if (searchText) {
            const queryClean = searchText.toLowerCase().trim();
            allRows = allRows.filter((row) => {
                const displayName = (row.displayName || `${row.firstName || ''} ${row.lastName || ''}`).toLowerCase();
                const email = (row.email || '').toLowerCase();
                const studentId = (row.studentId || '').toLowerCase();
                const grade = (row.academicInfo?.currentGrade || row.grade || row.className || '').toLowerCase();
                const roll = (row.academicInfo?.rollNumber || row.rollNumber || '').toLowerCase();
                return (
                    displayName.includes(queryClean) ||
                    email.includes(queryClean) ||
                    studentId.includes(queryClean) ||
                    grade.includes(queryClean) ||
                    roll.includes(queryClean)
                );
            });
        }

        return allRows;
    };

    const mapDataRowsForExport = (rawItems) => {
        return rawItems.map(item => {
            // Resolve Class/Grade
            const classVal = item.academicInfo?.currentGrade || item.grade || item.className || '';
            let resolvedClass = classVal;
            if (classVal && tenantClasses) {
                const foundClass = tenantClasses.find(c =>
                    c.id === classVal ||
                    c.name === classVal ||
                    (c.displayName && c.displayName.toLowerCase() === classVal.toLowerCase())
                );
                if (foundClass) {
                    resolvedClass = foundClass.displayName || [foundClass.grade, foundClass.section].filter(Boolean).join(' - ') || foundClass.name;
                }
            }

            // Resolve Badge
            const badgeId = item.academicInfo?.badgeId || '';
            let resolvedBadge = '—';
            if (badgeId && badgeRowById) {
                const brow = badgeRowById.get(badgeId);
                if (brow) {
                    const title = (brow.title || '').trim() || 'Untitled';
                    const campusLine = tenantHasCampusClasses && brow.branchId ? branchNameById.get(brow.branchId) || null : null;
                    resolvedBadge = `${title} (Badge ${brow.badgeNumber} | ${brow.year}${campusLine ? ` · ${campusLine}` : ''})`;
                }
            }

            // Return a shallow copy with resolved values
            return {
                ...item,
                grade: resolvedClass,
                className: resolvedClass,
                academicInfo: {
                    ...(item.academicInfo || {}),
                    currentGrade: resolvedClass,
                    badgeId: resolvedBadge,
                }
            };
        });
    };

    const onExportExcel = async (onProgress) => {
        const rawData = await handleFetchAllStudents(onProgress);
        const data = mapDataRowsForExport(rawData);
        exportToExcel(
            data,
            [
                { title: 'Student Name', dataIndex: 'displayName' },
                { title: 'Student ID', dataIndex: 'studentId' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Gender', dataIndex: 'gender' },
                { title: 'Class/Grade', dataIndex: 'academicInfo.currentGrade' },
                { title: 'Roll Number', dataIndex: 'academicInfo.rollNumber' },
                { title: 'Father Name', dataIndex: 'parentInfo.fatherName' },
                { title: 'Father Phone', dataIndex: 'parentInfo.fatherPhone' },
                { title: 'Status', dataIndex: 'status' },
            ],
            `students_${tenantId}.xlsx`
        );
    };

    const onExportPdf = async (onProgress) => {
        const rawData = await handleFetchAllStudents(onProgress);
        const data = mapDataRowsForExport(rawData);
        exportToPdf(
            data,
            [
                { title: 'Student Name', dataIndex: 'displayName' },
                { title: 'Student ID', dataIndex: 'studentId' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Gender', dataIndex: 'gender' },
                { title: 'Class/Grade', dataIndex: 'academicInfo.currentGrade' },
                { title: 'Roll Number', dataIndex: 'academicInfo.rollNumber' },
                { title: 'Father Name', dataIndex: 'parentInfo.fatherName' },
                { title: 'Father Phone', dataIndex: 'parentInfo.fatherPhone' },
                { title: 'Status', dataIndex: 'status' },
            ],
            'Students Report',
            `students_${tenantId}.pdf`
        );
    };

    const [emailManuallyEdited, setEmailManuallyEdited] = useState(false);
    const watchedFirstName = Form.useWatch('firstName', form);
    const watchedLastName = Form.useWatch('lastName', form);

    useEffect(() => {
        if (!watchedStudentId) {
            setStudentIdUnique(true);
            return;
        }
        const cleanVal = String(watchedStudentId).trim();
        if (!cleanVal) {
            setStudentIdUnique(true);
            return;
        }

        if (mode === 'edit' && editingStudent && String(editingStudent.studentId).trim() === cleanVal) {
            setStudentIdUnique(true);
            return;
        }

        const timer = setTimeout(async () => {
            setCheckingStudentId(true);
            try {
                const exists = await checkStudentIdExists({
                    tenantId,
                    studentId: cleanVal,
                    excludeStudentId: mode === 'edit' ? (editingStudent?.id || editingStudent?.authUid) : null,
                });
                setStudentIdUnique(!exists);
            } catch (err) {
                console.error(err);
            } finally {
                setCheckingStudentId(false);
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [watchedStudentId, mode, tenantId, editingStudent]);

    useEffect(() => {
        if (mode !== 'create' || emailManuallyEdited) return;

        const initials = getSchoolInitials(user?.schoolName || '');
        const schoolEmail = user?.email || '';
        const domain = schoolEmail.includes('@') ? schoolEmail.split('@')[1] : 'school.com';

        const cleanFirstName = String(watchedFirstName || '').trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const cleanLastName = String(watchedLastName || '').trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

        if (!cleanFirstName && !cleanLastName) {
            form.setFieldsValue({ email: '' });
            return;
        }

        const prefix = `${initials}.${cleanFirstName}${cleanLastName}`;
        const generatedEmail = `${prefix}@${domain.trim().toLowerCase()}`;
        form.setFieldsValue({ email: generatedEmail });
    }, [watchedFirstName, watchedLastName, mode, emailManuallyEdited, user, form]);

    const branchNameById = useMemo(() => {
        const m = new Map();
        m.set(STUDENT_BADGES_DEFAULT_BRANCH_ID, 'Default');
        for (const b of branches || []) m.set(b.id, (b.branchName || '').trim() || b.id);
        return m;
    }, [branches]);

    const createStudentMutation = useCreateStudent({ tenantId });
    const updateStudentMutation = useUpdateStudent({ tenantId });
    const { requestStatusChange, StatusChangeModal } = useDirectoryStatusChangeFlow();
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const showImportTools = import.meta.env.DEV;
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importProgress, setImportProgress] = useState({
        total: 0,
        processed: 0,
        success: 0,
        skipped: 0,
        running: false,
    });

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const name = (r.displayName || `${r.firstName || ''} ${r.lastName || ''}`).trim().toLowerCase();
            const email = (r.email || '').toLowerCase();
            const grade = (r.grade || r.className || '').toLowerCase();
            const studentId = String(r.studentId || '').toLowerCase();
            return name.includes(q) || email.includes(q) || grade.includes(q) || studentId.includes(q);
        });
    }, [rows, searchText]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    const classGradeOptions = useMemo(() => {
        return (tenantClasses || []).map((c) => {
            const label = c.displayName || [c.grade, c.section].filter(Boolean).join(' - ') || c.name || c.id;
            return { value: label, label };
        });
    }, [tenantClasses]);
    const classByLabel = useMemo(() => {
        const map = new Map();
        (tenantClasses || []).forEach((c) => {
            const label = c.displayName || [c.grade, c.section].filter(Boolean).join(' - ') || c.name || c.id;
            map.set(String(label || '').trim().toLowerCase(), c);
        });
        return map;
    }, [tenantClasses]);

    const watchedGrade = Form.useWatch(['academicInfo', 'currentGrade'], form);
    const tenantHasCampusClasses = useMemo(() => (tenantClasses || []).some((c) => c.branchId), [tenantClasses]);
    const classDocForBadgeFilter = useMemo(
        () => classByLabel.get(String(watchedGrade || '').trim().toLowerCase()),
        [classByLabel, watchedGrade]
    );
    const filteredBadgeFormOptions = useMemo(() => {
        if (!tenantHasCampusClasses) return badgeFormOptions;
        const bid = classDocForBadgeFilter?.branchId;
        if (!bid) return badgeFormOptions;
        return badgeFormOptions.filter((o) => !o.branchId || o.branchId === bid);
    }, [tenantHasCampusClasses, classDocForBadgeFilter, badgeFormOptions]);

    const defaultBadgeIdForForm = useMemo(() => {
        const opts = filteredBadgeFormOptions;
        if (!opts.length) return null;
        const y = new Date().getFullYear();
        const forYear = opts.filter((o) => Number(o.year) === y);
        const pool = forYear.length ? forYear : opts;
        const branchesInPool = new Set(pool.map((o) => o.branchId).filter(Boolean));
        if (branchesInPool.size > 1) return null;
        const sorted = [...pool].sort((a, b) => (Number(b.badgeNumber) || 0) - (Number(a.badgeNumber) || 0));
        return sorted[0]?.value ?? null;
    }, [filteredBadgeFormOptions]);

    useEffect(() => {
        if (!modalOpen || mode !== 'create' || !defaultBadgeIdForForm) return;
        const t = setTimeout(() => {
            const cur = form.getFieldValue('academicInfo') || {};
            if (!cur.badgeId) {
                form.setFieldsValue({ academicInfo: { ...cur, badgeId: defaultBadgeIdForForm } });
            }
        }, 0);
        return () => clearTimeout(t);
    }, [modalOpen, mode, defaultBadgeIdForForm, form, watchedGrade]);

    useEffect(() => {
        if (!modalOpen) return;
        const bid = form.getFieldValue(['academicInfo', 'badgeId']);
        if (!bid) return;
        const ok = filteredBadgeFormOptions.some((o) => o.value === bid);
        if (ok) return;
        const cur = form.getFieldValue('academicInfo') || {};
        form.setFieldsValue({ academicInfo: { ...cur, badgeId: undefined } });
    }, [modalOpen, filteredBadgeFormOptions, form]);

    const badgeDropdownOptions = useMemo(
        () =>
            filteredBadgeFormOptions.map((o) => ({
                value: o.value,
                label: (
                    <div style={{ lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 600 }}>{o.title}</div>
                        <div style={{ fontSize: 12, opacity: 0.65 }}>
                            Badge {o.badgeNumber} | {o.year}
                        </div>
                    </div>
                ),
            })),
        [filteredBadgeFormOptions]
    );

    const studentDisplayName = (row) => row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'Student';
    const isStudentActive = (r) => (r.status || 'active') !== 'inactive';
    const copyToClipboard = useCallback(async (value) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            message.success('Copied');
        } catch {
            message.error('Failed to copy');
        }
    }, []);

    const buildPhoneHref = useCallback((rawPhone) => {
        if (!rawPhone) return undefined;
        const raw = String(rawPhone).trim();
        const phoneForTel = raw.replace(/[^\d+]/g, '');
        const normalizeForWhatsApp = (value) => {
            let digits = String(value || '').replace(/\D/g, '');
            if (!digits) return '';
            if (digits.startsWith('00')) digits = digits.slice(2);
            if (digits.startsWith('0')) {
                return `${phoneCountryCode}${digits.slice(1)}`;
            }
            if (digits.length === 10) {
                return `${phoneCountryCode}${digits}`;
            }
            return digits;
        };
        if (phoneLinkMode === 'call') {
            return `tel:${phoneForTel}`;
        }
        if (phoneLinkMode === 'whatsapp' || phoneLinkMode === 'custom') {
            const template = phoneLinkTemplate || 'https://wa.me/{phone}';
            if (!template.includes('{phone}')) return template;
            const isWhatsAppTemplate =
                template.includes('wa.me') || template.includes('whatsapp.com') || template.startsWith('whatsapp:');
            const phoneForTemplate = isWhatsAppTemplate
                ? normalizeForWhatsApp(raw)
                : phoneForTel;
            return template.replace('{phone}', phoneForTemplate);
        }
        return `tel:${phoneForTel}`;
    }, [phoneCountryCode, phoneLinkMode, phoneLinkTemplate]);

    const handleStudentStatusSwitch = useCallback(async (row, nextActive) => {
        const cur = isStudentActive(row);
        if (nextActive === cur) return;
        const res = await requestStatusChange({
            displayName: studentDisplayName(row),
            nextActive,
        });
        if (!res.ok) return;
        const studentId = row.id || row.authUid;
        if (!studentId) {
            message.error('Missing student id');
            return;
        }
        setUpdatingStatusId(studentId);
        try {
            const patch = {
                status: nextActive ? 'active' : 'inactive',
                inactiveReason: nextActive ? null : res.reason,
            };
            if (row.academicInfo && typeof row.academicInfo === 'object') {
                patch.academicInfo = {
                    ...row.academicInfo,
                    status: nextActive ? 'active' : 'inactive',
                };
            }
            await updateStudentMutation.mutateAsync({ studentId, patch });
            message.success(nextActive ? 'Student activated' : 'Student deactivated');
        } catch (e) {
            message.error(e?.message || 'Failed to update status');
        } finally {
            setUpdatingStatusId(null);
        }
    }, [requestStatusChange, updateStudentMutation]);

    const columns = useMemo(() => ([
        {
            title: 'Student',
            key: 'student',
            render: (_, row) => {
                const displayName = row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';
                return (
                    <Space>
                        <AppAvatar
                            src={row.profileImage || row.avatarUrl || row.avatarDataUrl}
                            name={displayName}
                            seed={row?.id || row?.authUid || row?.email || displayName}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 4, alignItems: 'flex-start', textAlign: 'left' }}>
                            <Text strong style={{ lineHeight: 1.2, textAlign: 'left', width: '100%' }}>{displayName}</Text>
                            {row.email ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, textAlign: 'left' }}>
                                    <a
                                        href={`mailto:${row.email}`}
                                        className="app-link-cell"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {row.email}
                                    </a>
                                    <CopyOutlined
                                        className="app-link-copy"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void copyToClipboard(row.email);
                                        }}
                                    />
                                </span>
                            ) : (
                                <Text type="secondary" style={{ lineHeight: 1.2, fontSize: 12 }}>—</Text>
                            )}
                        </div>
                    </Space>
                );
            },
        },
        { title: 'Student ID', dataIndex: 'studentId', key: 'studentId', render: (v) => v || '—' },
        { title: 'Grade', dataIndex: 'grade', key: 'grade', render: (v, r) => v || r?.academicInfo?.currentGrade || r.className || '—' },
        {
            title: 'Father',
            key: 'father',
            render: (_, r) => {
                const fatherName = r?.parentInfo?.fatherName;
                const fatherPhone = r?.parentInfo?.fatherPhone;
                if (!fatherName && !fatherPhone) return '—';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', textAlign: 'left' }}>
                        <Text style={{ textAlign: 'left', width: '100%' }}>{fatherName || '—'}</Text>
                        {fatherPhone ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6, textAlign: 'left' }}>
                                <a
                                    href={buildPhoneHref(fatherPhone)}
                                    className="app-link-cell"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {fatherPhone}
                                </a>
                                <CopyOutlined
                                    className="app-link-copy"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void copyToClipboard(fatherPhone);
                                    }}
                                />
                            </span>
                        ) : null}
                    </div>
                );
            },
        },
        { title: 'Roll Number', key: 'rollNumber', render: (_, r) => r?.academicInfo?.rollNumber || '—' },
        {
            title: 'Date of Birth',
            key: 'dateOfBirth',
            render: (_, r) => {
                const dob = toDayjs(r?.dateOfBirth);
                return dob ? dob.format(dateFormat) : '—';
            },
        },
        {
            title: 'Admission Date',
            key: 'admissionDate',
            render: (_, r) => {
                const admissionDate = toDayjs(r?.academicInfo?.admissionDate);
                return admissionDate ? admissionDate.format(dateFormat) : '—';
            },
        },
        {
            title: 'Badge',
            key: 'badge',
            width: 160,
            render: (_, r) => {
                const bid = r?.academicInfo?.badgeId;
                if (!bid) return '—';
                const brow = badgeRowById.get(bid);
                if (!brow) return '—';
                const title = (brow.title || '').trim() || 'Untitled';
                const campusLine =
                    tenantHasCampusClasses && brow.branchId ? branchNameById.get(brow.branchId) || null : null;
                return (
                    <div style={{ lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 600 }}>{title}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Badge {brow.badgeNumber} | {brow.year}
                            {campusLine ? ` · ${campusLine}` : ''}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, row) => {
                const active = isStudentActive(row);
                const tip = !active && row.inactiveReason ? row.inactiveReason : active ? 'Active' : 'Inactive';
                const sid = row.id || row.authUid;
                return (
                    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <Tooltip title={tip}>
                            <Switch
                                checked={active}
                                loading={updatingStatusId === sid}
                                onChange={(checked) => void handleStudentStatusSwitch(row, checked)}
                            />
                        </Tooltip>
                    </span>
                );
            },
        },
    ]), [
        badgeRowById,
        branchNameById,
        buildPhoneHref,
        copyToClipboard,
        dateFormat,
        handleStudentStatusSwitch,
        tenantHasCampusClasses,
        updatingStatusId,
    ]);

    const handleOpenCreate = async () => {
        setMode('create');
        setEditingStudent(null);
        setProfileImagePreview(null);
        setEmailManuallyEdited(false);
        form.resetFields();
        setModalOpen(true);
        try {
            const nextId = await generateNextStudentId(tenantId, user?.schoolName);
            form.setFieldsValue({ studentId: nextId });
        } catch (err) {
            console.error('Failed to generate student ID:', err);
        }
    };

    const handleImportStudents = useCallback(async (file) => {
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const sheetName = workbook.SheetNames?.[0];
            if (!sheetName) throw new Error('No worksheet found');
            const sheet = workbook.Sheets[sheetName];
            const rowsRaw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            if (!rowsRaw.length) throw new Error('No data rows found');

            const requiredColumns = [
                'first name',
                'gender',
                'date of birth',
                'current grade',
            ];

            const mappedRows = rowsRaw.map((raw) => {
                const row = {};
                Object.entries(raw).forEach(([key, value]) => {
                    row[normalizeHeaderKey(key)] = value;
                });
                return row;
            });

            const headerKeys = new Set(Object.keys(mappedRows[0] || {}));
            const missingColumns = requiredColumns.filter((col) => !headerKeys.has(col));
            if (missingColumns.length) {
                throw new Error(`Missing column(s): ${missingColumns.join(', ')}`);
            }

            setImportModalOpen(true);
            setImportProgress({
                total: mappedRows.length,
                processed: 0,
                success: 0,
                skipped: 0,
                running: true,
            });

            const existingStudents = await fetchStudentsOnce({ tenantId });
            const initials = getSchoolInitials(user?.schoolName);
            const year = dayjs().year();
            const prefix = `${initials}-${year}-`;
            let maxSerial = 1000;
            const regex = new RegExp(`^${initials}-${year}-(\\d+)$`);
            existingStudents.forEach((s) => {
                const sid = s.studentId;
                if (sid) {
                    const match = sid.match(regex);
                    if (match) {
                        const num = parseInt(match[1], 10);
                        if (num > maxSerial) {
                            maxSerial = num;
                        }
                    }
                }
            });

            const skippedRows = [];
            let success = 0;
            let skipped = 0;

            for (let i = 0; i < mappedRows.length; i += 1) {
                const row = mappedRows[i];
                const rowNumber = i + 2;
                try {
                    let studentId = String(row['student id'] || '').trim();
                    if (!studentId) {
                        maxSerial += 1;
                        studentId = `${prefix}${maxSerial}`;
                    }

                    const firstName = String(row['first name'] || '').trim();
                    const lastName = String(row['last name'] || '').trim();
                    const dateOfBirthRaw = row['date of birth'];
                    const fatherName = String(row['father name'] || '').trim();
                    const fatherPhone = String(row['father phone'] || '').trim();
                    const currentGrade = String(row['current grade'] || '').trim();
                    const section = String(row['section'] || '').trim();
                    const rollNumber = String(row['roll number'] || '').trim();
                    const admissionDateRaw = row['admission date'];
                    const email = String(row.email || '').trim();
                    const password = String(row.password || '').trim();
                    const gender = String(row.gender || '').trim().toLowerCase();

                    if (!firstName || !gender || !dateOfBirthRaw || !currentGrade) {
                        throw new Error('First Name, Gender, Date of Birth, and Current Grade are required');
                    }
                    if (!['male', 'female', 'other'].includes(gender)) {
                        throw new Error('Gender must be male, female, or other');
                    }
                    if (password && password.length < 8) {
                        throw new Error('Password must be at least 8 characters');
                    }

                    const hasDateOfBirth = !(dateOfBirthRaw === '' || dateOfBirthRaw === null || dateOfBirthRaw === undefined);
                    const dateOfBirth = hasDateOfBirth ? parseImportDate(dateOfBirthRaw, dateInputFormats) : null;
                    if (hasDateOfBirth && (!dateOfBirth || !dayjs(dateOfBirth).isValid())) {
                        throw new Error(`Date of Birth must match tenant format (${dateFormat})`);
                    }

                    const admissionDate = admissionDateRaw
                        ? parseImportDate(admissionDateRaw, dateInputFormats)
                        : dayjs().toDate();
                    if (admissionDateRaw && (!admissionDate || !dayjs(admissionDate).isValid())) {
                        throw new Error(`Admission Date must match tenant format (${dateFormat})`);
                    }

                    const classDoc = classByLabel.get(currentGrade.toLowerCase());
                    if (!classDoc?.id) {
                        throw new Error(`Class not found for Current Grade "${currentGrade}"`);
                    }

                    const defaultBidForRow =
                        defaultBadgeId ||
                        (() => {
                            const y = badgeYearDefault;
                            const pool = (badgeRows || []).filter(
                                (b) =>
                                    Number(b.year) === y &&
                                    (!classDoc.branchId || !b.branchId || b.branchId === classDoc.branchId)
                            );
                            const sorted = [...pool].sort((a, b) => (Number(b.badgeNumber) || 0) - (Number(a.badgeNumber) || 0));
                            return sorted[0]?.id || null;
                        })();

                    const badgeId = resolveBadgeIdFromImportRow(row, badgeRows, {
                        defaultBadgeId: defaultBidForRow,
                        defaultCalendarYear: badgeYearDefault,
                        classBranchId: classDoc.branchId ?? null,
                    });
                    if (!badgeId) {
                        throw new Error('Assign a badge: add Badge # (and optional Badge Year) columns, or create badges under Classes & Badge');
                    }

                    await createStudentMutation.mutateAsync({
                        email: email || undefined,
                        password: password || undefined,
                        profile: {
                            studentId,
                            firstName,
                            lastName,
                            dateOfBirth,
                            displayName: `${firstName} ${lastName}`.trim() || lastName,
                            gender,
                            phone: null,
                            parentInfo: {
                                fatherName,
                                fatherPhone,
                            },
                            academicInfo: {
                                currentGrade,
                                classId: classDoc.id,
                                section,
                                rollNumber,
                                admissionDate,
                                status: 'active',
                                badgeId,
                            },
                            status: 'active',
                        },
                    });
                    success += 1;
                } catch (err) {
                    skipped += 1;
                    skippedRows.push({
                        rowNumber,
                        reason: err?.message || 'Unknown error',
                        studentId: row['student id'],
                        firstName: row['first name'],
                        lastName: row['last name'],
                        dateOfBirth: row['date of birth'],
                        fatherName: row['father name'],
                        fatherPhone: row['father phone'],
                        currentGrade: row['current grade'],
                        section: row.section,
                        rollNumber: row['roll number'],
                        admissionDate: row['admission date'],
                        email: row.email,
                        password: row.password,
                        gender: row.gender,
                        'Badge #': row['badge number'] ?? row['badge #'],
                        'Badge Year': row['badge year'],
                    });
                } finally {
                    setImportProgress((prev) => ({
                        ...prev,
                        processed: i + 1,
                        success,
                        skipped,
                    }));
                }
            }

            if (skippedRows.length) {
                const skippedSheet = XLSX.utils.json_to_sheet(skippedRows);
                const skippedBook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(skippedBook, skippedSheet, 'SkippedRows');
                XLSX.writeFile(skippedBook, `students-import-skipped-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`);
            }

            setImportProgress((prev) => ({ ...prev, running: false }));
            message.success(`Import complete. Success: ${success}, Skipped: ${skipped}`);
        } catch (err) {
            setImportModalOpen(false);
            message.error(err?.message || 'Failed to import students');
        }
        return false;
    }, [badgeRows, badgeYearDefault, classByLabel, createStudentMutation, dateFormat, dateInputFormats, defaultBadgeId, tenantId, user]);

    const handleDownloadImportTemplateExcel = useCallback(() => {
        const sampleBadge = (badgeRows || []).find((b) => b.id === defaultBadgeId) || (badgeRows || [])[0];
        const badgeSample =
            sampleBadge != null
                ? {
                    'Badge #': Number(sampleBadge.badgeNumber) || sampleBadge.badgeNumber,
                    'Badge Year': Number(sampleBadge.year) || sampleBadge.year,
                }
                : {
                    'Badge #': '',
                    'Badge Year': '',
                };
        const rows = [
            {
                'Student ID': 'STU-001',
                'First Name': 'John',
                'Last Name': 'Doe',
                'Date of Birth': dayjs().subtract(10, 'year').format(dateFormat),
                'Father Name': 'Michael Doe',
                'Father Phone': '+923001234567',
                'Current Grade': classGradeOptions?.[0]?.label || 'Grade 10',
                Section: 'A',
                'Roll Number': '23',
                'Admission Date': dayjs().format(dateFormat),
                ...badgeSample,
                Email: 'john.doe@example.com',
                Password: 'P@ssword123',
                Gender: 'male',
            },
        ];
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'StudentsTemplate');
        XLSX.writeFile(workbook, 'student-import-template.xlsx');
    }, [badgeRows, badgeYearDefault, classGradeOptions, dateFormat, defaultBadgeId]);

    const handleSubmit = async () => {
        if (!studentIdUnique) {
            message.error('Student ID is already in use');
            return;
        }
        if (checkingStudentId) {
            message.warning('Please wait while verifying Student ID uniqueness');
            return;
        }
        const values = await form.validateFields();
        const { email, password, confirmPassword: _confirmPassword, ...profile } = values;

        const gradeLabel = profile.academicInfo?.currentGrade;
        const classDoc = classByLabel.get(String(gradeLabel || '').trim().toLowerCase());
        const profilePayload = {
            ...profile,
            academicInfo: {
                ...profile.academicInfo,
                ...(classDoc?.id ? { classId: classDoc.id } : {}),
                admissionDate: profile.academicInfo?.admissionDate || dayjs(),
            },
            profileImage: profileImagePreview || null,
            displayName:
                (profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`).trim() || null,
        };

        try {
            if (mode === 'create') {
                const res = await createStudentMutation.mutateAsync({
                    email: email || undefined,
                    password: password || undefined,
                    profile: profilePayload,
                });
                message.success(`Student created: ${res.email}`);
            } else {
                const studentId = editingStudent?.id || editingStudent?.authUid;
                if (!studentId) throw new Error('Missing student id');
                await updateStudentMutation.mutateAsync({
                    studentId,
                    patch: profilePayload,
                });
                message.success('Student updated');
            }
            setModalOpen(false);
            form.resetFields();
            setProfileImagePreview(null);
            setEmailManuallyEdited(false);
            setMode('create');
            setEditingStudent(null);
        } catch (e) {
            message.error(e?.message || (mode === 'create' ? 'Failed to create student' : 'Failed to update student'));
        }
    };

    const openEdit = (row) => {
        setMode('edit');
        setEditingStudent(row);
        setModalOpen(true);
    };

    // When the modal opens in edit mode, the form mounts (destroyOnClose + preserve={false}),
    // so setFieldsValue must happen AFTER open.
    useEffect(() => {
        if (!modalOpen || mode !== 'edit' || !editingStudent) return;
        const row = editingStudent;
        // Form mounts after open (destroyOnClose + preserve={false}), so defer to next tick.
        setTimeout(() => {
            setProfileImagePreview(row?.profileImage || row?.avatarUrl || row?.avatarDataUrl || null);
            form.resetFields();
            form.setFieldsValue({
                ...row,
                dateOfBirth: toDayjs(row?.dateOfBirth),
                address: row?.address || {},
                parentInfo: row?.parentInfo || {},
                academicInfo: {
                    ...(row?.academicInfo || {}),
                    // Ensure required fields match the create form shape
                    currentGrade: row?.academicInfo?.currentGrade || row?.grade || row?.className || '',
                    section: row?.academicInfo?.section || '',
                    rollNumber: row?.academicInfo?.rollNumber || '',
                    admissionDate: toDayjs(row?.academicInfo?.admissionDate),
                    status: row?.academicInfo?.status || 'active',
                    badgeId: row?.academicInfo?.badgeId || undefined,
                },
                status: row?.status || 'active',
            });
        }, 0);
    }, [modalOpen, mode, editingStudent, form]);

    // (Row click edit now reuses the create modal; detail modal removed)

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Students</StudentsHeaderTitle>
                    <StudentsHeaderSub>Manage student accounts and profiles for your school.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <StudentsHeaderActions>
                    {showImportTools ? (
                        <>
                            <Upload
                                accept=".xlsx,.xls"
                                showUploadList={false}
                                beforeUpload={handleImportStudents}
                            >
                                <Button $primary={false} icon={<UploadOutlined />}>
                                    Import
                                </Button>
                            </Upload>
                            <Tooltip
                                title={
                                    <span>
                                        Template includes <strong>Badge #</strong> and <strong>Badge Year</strong> (must match a
                                        badge in Classes & Badge). Leave both cells empty to use the default badge for this year.
                                        If you only enter Badge #, the year defaults to {badgeYearDefault}.
                                    </span>
                                }
                            >
                                <Button $primary={false} icon={<FileExcelOutlined />} onClick={handleDownloadImportTemplateExcel}>
                                    Sample Excel
                                </Button>
                            </Tooltip>
                        </>
                    ) : null}
                    <Tooltip title={limitExceeded ? "Limit is exceeded. Please contact EDULINK to change your Subscription Plan." : ""}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleOpenCreate}
                            disabled={limitExceeded}
                        >
                            Create Student
                        </Button>
                    </Tooltip>
                </StudentsHeaderActions>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={3} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Total Students" value={`${kpis.totalStudents ?? 0}`} />
                <KPICard title="Active Students" value={`${kpis.activeStudents ?? 0}`} />
                <KPICard title="Inactive Students" value={`${kpis.inactiveStudents ?? 0}`} />
            </KPICardsGrid>

            <Table
                title="Student Information"
                columns={columns}
                dataSource={filteredRows}
                loading={loading}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showFilter={false}
                pagination={true}
                pageSize={currentPageSize}
                serverPagination
                currentPage={currentPage}
                totalItems={searchText ? filteredRows.length : total}
                onPageChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== currentPageSize) {
                        setCurrentPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                showSearch={true}
                showPagination={true}
                showDownload={showDownload}
                onExportExcel={onExportExcel}
                onExportPdf={onExportPdf}
                onRowClick={openEdit}
            />

            {StatusChangeModal}
            <StudentRegisterModalStyles />
            <AppModal
                title={<span style={{ fontWeight: 800 }}>Importing Students</span>}
                open={importModalOpen}
                onCancel={() => {
                    if (importProgress.running) return;
                    setImportModalOpen(false);
                }}
                onOk={() => setImportModalOpen(false)}
                okText="Close"
                cancelButtonProps={{ style: { display: importProgress.running ? 'none' : undefined } }}
                okButtonProps={{ disabled: importProgress.running }}
                width={560}
            >
                <div style={{ display: 'grid', gap: 10 }}>
                    <Text type="secondary">
                        Processed {importProgress.processed} of {importProgress.total} rows
                    </Text>
                    <Progress
                        percent={importProgress.total ? Math.round((importProgress.processed / importProgress.total) * 100) : 0}
                        status={importProgress.running ? 'active' : 'success'}
                    />
                    <Text>Success: {importProgress.success}</Text>
                    <Text>Skipped: {importProgress.skipped}</Text>
                    {importProgress.running ? (
                        <Text type="secondary">Please wait while we create auth users and student records...</Text>
                    ) : (
                        <Text type="secondary">Import finished. If any rows were skipped, an Excel report was downloaded.</Text>
                    )}
                </div>
            </AppModal>
            <AppModal
                title={
                    mode === 'create'
                        ? <span style={{ color: '#059669', fontWeight: 800 }}>Add New Student</span>
                        : <span style={{ fontWeight: 800 }}>Edit Student</span>
                }
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setMode('create');
                    setEditingStudent(null);
                    setProfileImagePreview(null);
                    setEmailManuallyEdited(false);
                    form.resetFields();
                }}
                onOk={handleSubmit}
                confirmLoading={mode === 'create' ? createStudentMutation.isPending : updateStudentMutation.isPending}
                okText={mode === 'create' ? 'Save student' : 'Save changes'}
                size="lg"
                width="86vw"
                style={{ maxWidth: 960 }}
                className="student-register-modal"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    preserve={false}
                    initialValues={{
                        status: 'active',
                        gender: 'male',
                        academicInfo: {
                            status: 'active',
                        },
                    }}
                >
                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <Users size={18} />
                            </CardHeaderIcon>
                            Basic Information
                        </CardHeader>

                        <Row gutter={16}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name="studentId"
                                    label="Student ID"
                                    validateStatus={checkingStudentId ? 'validating' : !studentIdUnique ? 'error' : 'success'}
                                    help={checkingStudentId ? 'Checking uniqueness...' : !studentIdUnique ? 'Student ID is already in use' : ''}
                                    rules={[
                                        { required: true, message: 'Student ID is required' },
                                        () => ({
                                            validator() {
                                                if (studentIdUnique) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Student ID is already in use'));
                                            }
                                        })
                                    ]}
                                >
                                    <AppInput placeholder="Enter unique student ID" size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'First name is required' }]}>
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="lastName" label="Last Name">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Valid email required' }]}
                                >
                                    <AppInput
                                        placeholder="Enter email address (optional)"
                                        size="large"
                                        autoComplete="off"
                                        disabled={mode !== 'create'}
                                        onChange={() => setEmailManuallyEdited(true)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="phone" label="Phone">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="dateOfBirth" label="Date of Birth" rules={[{ required: true, message: 'Date of birth is required' }]}>
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Gender is required' }]}>
                                    <AppSelect
                                        options={[
                                            { value: 'male', label: 'Male' },
                                            { value: 'female', label: 'Female' },
                                            { value: 'other', label: 'Other' },
                                        ]}
                                        placeholder="Male"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item label="Profile Image">
                                    <ProfileImageRow>
                                        <ProfileImagePreview>
                                            {profileImagePreview ? (
                                                <img src={profileImagePreview} alt="Profile preview" />
                                            ) : (
                                                <UserOutlined style={{ fontSize: 26, opacity: 0.45 }} />
                                            )}
                                        </ProfileImagePreview>
                                        <div>
                                            <Upload
                                                accept="image/*"
                                                maxCount={1}
                                                showUploadList={false}
                                                beforeUpload={async (file) => {
                                                    if (!file.type?.startsWith?.('image/')) {
                                                        message.error('Please select an image file');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        message.error('Please select an image smaller than 5MB');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    const dataUrl = await toDataUrl(file);
                                                    setProfileImagePreview(dataUrl);
                                                    return false;
                                                }}
                                            >
                                                <Button $primary={false} icon={<UploadOutlined />}>
                                                    Upload Image
                                                </Button>
                                            </Upload>
                                            <UploadHelp>JPG, PNG or GIF (Max 5MB)</UploadHelp>
                                        </div>
                                    </ProfileImageRow>
                                </Form.Item>
                            </Col>

                            {mode === 'create' ? (
                                <>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="password" label="Password" rules={[{ min: 8, message: 'Password must be at least 8 characters' }]}>
                                            <AppPasswordInput placeholder="Create a secure password (optional)" size="large" autoComplete="new-password" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="confirmPassword"
                                            label="Confirm Password"
                                            dependencies={['password']}
                                            rules={[
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        if (!getFieldValue('password') || getFieldValue('password') === value) return Promise.resolve();
                                                        return Promise.reject(new Error('Passwords do not match'));
                                                    },
                                                }),
                                            ]}
                                        >
                                            <AppPasswordInput placeholder="Confirm your password (optional)" size="large" />
                                        </Form.Item>
                                    </Col>
                                </>
                            ) : null}
                        </Row>
                    </FormSectionCard>

                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <Home size={18} />
                            </CardHeaderIcon>
                            Address Information
                        </CardHeader>

                        <Row gutter={16}>
                            <Col xs={24}>
                                <Form.Item
                                    name={['address', 'street']}
                                    label="Street Address"
                                >
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['address', 'city']} label="City">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['address', 'state']} label="State">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['address', 'zipCode']} label="ZIP Code">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['address', 'country']} label="Country">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <Users size={18} />
                            </CardHeaderIcon>
                            Parent Information
                        </CardHeader>

                        <Row gutter={16}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['parentInfo', 'fatherName']}
                                    label="Father's Name"
                                >
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['parentInfo', 'motherName']} label="Mother's Name">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['parentInfo', 'fatherPhone']}
                                    label="Father's Phone"
                                >
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['parentInfo', 'fatherEmail']} label="Father's Email">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['parentInfo', 'fatherOccupation']}
                                    label="Father's Occupation"
                                >
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['parentInfo', 'fatherWorkPlace']}
                                    label="Father's Work Place"
                                >
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['parentInfo', 'motherOccupation']} label="Mother's Occupation">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['parentInfo', 'motherWorkPlace']} label="Mother's Work Place">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <GraduationCap size={18} />
                            </CardHeaderIcon>
                            Current Academic Information
                        </CardHeader>

                        <Row gutter={16}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['academicInfo', 'currentGrade']}
                                    label="Current grade"
                                    rules={[{ required: true, message: 'Current grade is required' }]}
                                >
                                    <AppSelect
                                        options={classGradeOptions}
                                        placeholder="Select class/grade"
                                        showSearch
                                        optionFilterProp="label"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['academicInfo', 'badgeId']}
                                    label="Badge"
                                    required={mode === 'create'}
                                    rules={mode === 'create' ? [{ required: true, message: 'Select a badge' }] : []}
                                    extra={
                                        mode === 'create' && !badgesLoading && !badgeRows.length ? (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Create badges under{' '}
                                                <RouterLink to="/tenant/classes-subjects">Classes & Badge</RouterLink>, then select one here.
                                            </Text>
                                        ) : null
                                    }
                                >
                                    <AppSelect
                                        options={badgeDropdownOptions}
                                        placeholder={badgesLoading ? 'Loading badges…' : 'Search or select badge'}
                                        showSearch
                                        filterOption={(input, option) => {
                                            const row = filteredBadgeFormOptions.find((b) => b.value === option?.value);
                                            if (!row) return false;
                                            const q = (input || '').trim().toLowerCase();
                                            const hay = `${row.title} ${row.badgeNumber} ${row.year}`.toLowerCase();
                                            return hay.includes(q);
                                        }}
                                        size="large"
                                        loading={badgesLoading}
                                        notFoundContent={
                                            !badgeRows.length ? (
                                                <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <Text type="secondary">No badges yet. </Text>
                                                    <RouterLink to="/tenant/classes-subjects">Create under Classes & Badge</RouterLink>
                                                </div>
                                            ) : tenantHasCampusClasses && !classDocForBadgeFilter?.branchId ? (
                                                <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <Text type="secondary">Select current grade to list campus badges.</Text>
                                                </div>
                                            ) : !filteredBadgeFormOptions.length ? (
                                                <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <Text type="secondary">No badges for this campus. </Text>
                                                    <RouterLink to="/tenant/classes-subjects">Create under Classes & Badge</RouterLink>
                                                </div>
                                            ) : (
                                                <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <Text type="secondary">No matching badges</Text>
                                                </div>
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['academicInfo', 'rollNumber']}
                                    label="Roll number"
                                >
                                    <AppInput placeholder="Roll number" size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['academicInfo', 'admissionDate']}
                                    label="Admission date"
                                >
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name={['academicInfo', 'status']}
                                    label="Academic status"
                                    rules={[{ required: true, message: 'Academic status is required' }]}
                                >
                                    <AppSelect
                                        options={[
                                            { value: 'active', label: 'Active' },
                                            { value: 'inactive', label: 'Inactive' },
                                            { value: 'graduated', label: 'Graduated' },
                                        ]}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <GraduationCap size={18} />
                            </CardHeaderIcon>
                            Academic History
                        </CardHeader>

                        <Form.List name={['academicInfo', 'gradeHistory']}>
                            {(fields, { add, remove }) => (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 13 }}>History records</Text>
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                add({
                                                    status: 'completed',
                                                    marks: { totalMarks: 0, obtainedMarks: 0, percentage: 0 },
                                                })
                                            }
                                        >
                                            Add record
                                        </Button>
                                    </div>
                                    {fields.map((field) => (
                                        <div
                                            key={field.key}
                                            style={{
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                borderRadius: 12,
                                                padding: 12,
                                                marginBottom: 10,
                                            }}
                                        >
                                            <Row gutter={12}>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'grade']} label="Grade">
                                                        <AppInput placeholder="e.g. Grade 9" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'academicYear']} label="Academic year">
                                                        <AppInput placeholder="e.g. 2025-2026" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'status']} label="Status">
                                                        <AppSelect
                                                            options={[
                                                                { value: 'completed', label: 'Completed' },
                                                                { value: 'promoted', label: 'Promoted' },
                                                                { value: 'failed', label: 'Failed' },
                                                            ]}
                                                            size="large"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'section']} label="Section">
                                                        <AppInput placeholder="e.g. A" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'rollNumber']} label="Roll number">
                                                        <AppInput placeholder="Roll number" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'remarks']} label="Remarks">
                                                        <AppInput placeholder="Optional" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'marks', 'totalMarks']} label="Total marks">
                                                        <AppInput inputMode="numeric" placeholder="0" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} md={8}>
                                                    <Form.Item {...field} name={[field.name, 'marks', 'obtainedMarks']} label="Obtained marks">
                                                        <AppInput inputMode="numeric" placeholder="0" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Button $primary={false} danger type="button" onClick={() => remove(field.name)}>
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                            {field.key !== fields[fields.length - 1]?.key ? (
                                                <div style={{ marginTop: 12, borderTop: '1px solid rgba(138, 152, 167, 0.22)' }} />
                                            ) : null}
                                        </div>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </FormSectionCard>

                    <FormSectionCard>
                        <CardHeader>
                            <CardHeaderIcon>
                                <PhoneCall size={18} />
                            </CardHeaderIcon>
                            Emergency Contact
                        </CardHeader>

                        <Row gutter={16}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['emergencyContact', 'name']} label="Contact Name">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['emergencyContact', 'relationship']} label="Relationship">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['emergencyContact', 'phone']} label="Phone">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['emergencyContact', 'email']} label="Email">
                                    <AppInput size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>
                </Form>
            </AppModal>

        </DashboardContainer>
    );
};

export default Students;

